import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const MagazineGrid = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const productImage = Array.isArray(product?.image_url)
    ? product.image_url[0]
    : product?.image_url || "";

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  // All segment attributes filtered by visibleFields
  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-magazine-grid">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Decorative: diagonal stripe top-left ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, width: 140, height: 140,
        background: "var(--theme-accent)", opacity: 0.12,
        transform: "rotate(45deg) translate(-60px, -60px)", zIndex: 1,
      }} />
      {/* ── Decorative: circles bottom-right ── */}
      <div style={{
        position: "absolute", bottom: -60, right: -60, width: 200, height: 200,
        borderRadius: "50%", border: "2px solid var(--theme-accent)", opacity: 0.18, zIndex: 1,
      }} />
      <div style={{
        position: "absolute", bottom: -40, right: -40, width: 140, height: 140,
        borderRadius: "50%", background: "var(--theme-accent)", opacity: 0.07, zIndex: 1,
      }} />

      {/* ── Accent top bar ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 5,
        background: "var(--theme-accent)", zIndex: 10,
      }} />

      {/* ── Brand bar ── */}
      <div style={{
        position: "absolute", top: 5, left: 0, right: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 16px",
        background: "var(--theme-surface)",
        borderBottom: "1px solid var(--theme-border)",
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isVisible("logoUrl") && buyer?.logoUrl && (
            <img src={buyer.logoUrl} alt="Logo"
              style={{ width: 26, height: 26, objectFit: "contain", borderRadius: 4 }} />
          )}
          {isVisible("organizationName") && (
            <span style={{
              fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
              fontSize: "0.78rem", fontWeight: 800,
              color: "var(--theme-text)",
              textTransform: "uppercase", letterSpacing: "0.1em",
            }}>
              {buyer?.organizationName || "YOUR BRAND"}
            </span>
          )}
        </div>
        <div style={{
          background: "var(--theme-accent)", color: "var(--theme-bg)",
          fontSize: "0.6rem", fontWeight: 700,
          padding: "3px 10px", borderRadius: 20,
          letterSpacing: "0.1em",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
        }}>
          {product?.segment?.toUpperCase() || "COLLECTION"}
        </div>
      </div>

      {/* ── 2×2 image collage ── */}
      <div style={{
        position: "absolute", top: 50, left: 0, right: 0,
        height: "46%",
        display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr",
        gap: 2, zIndex: 2,
      }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ overflow: "hidden", background: "var(--theme-surface)" }}>
            <img src={productImage} alt="" crossOrigin="anonymous"
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: "block",
                filter: i > 0 ? "brightness(0.85)" : "none",
              }} />
          </div>
        ))}
        {/* gradient fade */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, transparent 40%, var(--theme-bg) 100%)",
          pointerEvents: "none",
        }} />
      </div>

      {/* ── Info block (bottom half) ── */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, top: "46%",
        display: "flex", flexDirection: "column",
        padding: "10px 16px 12px",
        zIndex: 5,
      }}>

        {/* Product name */}
        <h2 style={{
          fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
          fontSize: "1.1rem", fontWeight: 800,
          color: "var(--theme-text)",
          margin: "0 0 6px", lineHeight: 1.2,
        }}>
          {product?.name || "Fine Jewellery"}
        </h2>

        {/* Accent divider */}
        <div style={{
          height: 2, background: "var(--theme-accent)",
          width: 40, marginBottom: 8, borderRadius: 2,
        }} />

        {/* ── All segment attributes grid ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 10px",
            background: "var(--theme-surface)",
            border: "1px solid var(--theme-border)",
            borderRadius: 4,
            padding: "7px 10px",
            marginBottom: 8,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.52rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                    <DiamondShapeSVG
                      shapeName={String(v)}
                      size={26}
                      color="var(--theme-accent)"
                    />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600,
                      color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>
                      {fmtVal(v)}
                    </span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.68rem", fontWeight: 600,
                    color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>
                    {fmtVal(v)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {product?.description && (
          <p style={{
            fontSize: "0.64rem", lineHeight: 1.4,
            color: "var(--theme-textMuted)",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            margin: "0 0 6px",
          }}>
            {product.description.length > 80
              ? product.description.slice(0, 80) + "…"
              : product.description}
          </p>
        )}

        <div style={{ flex: 1 }} />

        {/* Price + CTA */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "1px solid var(--theme-border)", paddingTop: 8,
        }}>
          <div>
            <div style={{
              fontSize: "0.52rem", fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.1em", color: "var(--theme-textMuted)",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)", marginBottom: 2,
            }}>Price</div>
            <div style={{
              fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
              fontSize: "1rem", fontWeight: 800, color: "var(--theme-accent)",
            }}>
              {formatPrice(price)}
            </div>
          </div>
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.65rem", fontWeight: 800,
            padding: "7px 14px", borderRadius: 4,
            letterSpacing: "0.1em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            SHOP NOW
          </div>
        </div>

        {/* Footer contacts */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 6, paddingTop: 6,
          borderTop: "1px solid var(--theme-border)",
        }}>
          {isVisible("address") && (
            <span style={{
              fontSize: "0.52rem", color: "var(--theme-textMuted)",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            }}>
              📍 {buyer?.streetAddress || "123 Jewellers Lane"}
            </span>
          )}
          {isVisible("phone") && (
            <span style={{
              fontSize: "0.52rem", color: "var(--theme-textMuted)",
              fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            }}>
              📞 {buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

MagazineGrid.displayName = "MagazineGrid";
export default MagazineGrid;
